import React, { useState, useMemo } from 'react';
import { 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TableSortLabel,
  LinearProgress
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Build as BuildIcon
} from '@mui/icons-material';

/**
 * AutomationStatusTable component for displaying test cases with automation status as rows and test suites as columns
 * @param {Object} props - Component props
 * @param {Array} props.summaryData - Array of automation summary data objects
 * @param {Array} props.testSuites - Array of test suite names for dynamic columns
 * @param {Object} props.filters - Available filter options
 * @param {Function} props.onFilterChange - Callback for filter changes
 * @returns {JSX.Element} AutomationStatusTable component
 */
const AutomationStatusTable = ({ summaryData = [], testSuites = [], filters = {}, onFilterChange }) => {
  const [selectedTestSuite, setSelectedTestSuite] = useState('all');
  const [selectedAutomationStatus, setSelectedAutomationStatus] = useState('all');
  const [orderBy, setOrderBy] = useState('automationStatus');
  const [order, setOrder] = useState('asc');

  // Handle filter changes
  const handleTestSuiteChange = (event) => {
    const value = event.target.value;
    setSelectedTestSuite(value);
    if (onFilterChange) {
      onFilterChange({ testSuite: value, automationStatus: selectedAutomationStatus });
    }
  };

  const handleAutomationStatusChange = (event) => {
    const value = event.target.value;
    setSelectedAutomationStatus(value);
    if (onFilterChange) {
      onFilterChange({ testSuite: selectedTestSuite, automationStatus: value });
    }
  };

  // Handle sorting
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort data
  const sortedData = useMemo(() => {
    return [...summaryData].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Handle string sorting
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [summaryData, orderBy, order]);

  // Render automation status chip
  const renderAutomationStatusChip = (percentage) => {
    let color = 'default';
    let icon = null;
    let label = `${percentage}%`;

    if (percentage >= 80) {
      color = 'success';
      icon = <CheckCircleIcon fontSize="small" />;
    } else if (percentage >= 50) {
      color = 'warning';
      icon = <BuildIcon fontSize="small" />;
    } else {
      color = 'error';
      icon = <CancelIcon fontSize="small" />;
    }

    return (
      <Chip
        icon={icon}
        label={label}
        color={color}
        size="small"
        variant="outlined"
      />
    );
  };

  // Render progress bar for automation percentage
  const renderProgressBar = (percentage) => {
    let color = 'error';
    if (percentage >= 80) {
      color = 'success';
    } else if (percentage >= 50) {
      color = 'warning';
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: '100px' }}>
          <LinearProgress 
            variant="determinate" 
            value={percentage} 
            color={color}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {percentage}%
        </Typography>
      </Box>
    );
  };

  // Create sortable table header
  const createSortHandler = (property) => () => {
    handleRequestSort(property);
  };

  // Calculate totals for each test suite
  const testSuiteTotals = {};
  let grandTotal = 0;

  testSuites.forEach(suite => {
    testSuiteTotals[suite] = 0;
  });

  summaryData.forEach(row => {
    if (row && row.totalCount) {
      grandTotal += row.totalCount;
    }
    if (row && row.testSuiteCounts && typeof row.testSuiteCounts === 'object') {
      Object.keys(row.testSuiteCounts).forEach(suite => {
        if (testSuiteTotals[suite] !== undefined) {
          testSuiteTotals[suite] += row.testSuiteCounts[suite] || 0;
        }
      });
    }
  });

  return (
    <Box>
      {/* Filters - Always visible */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Test Suite</InputLabel>
            <Select
              value={selectedTestSuite}
              label="Test Suite"
              onChange={handleTestSuiteChange}
            >
              <MenuItem value="all">All Test Suites</MenuItem>
              {filters.testSuites?.map((suite) => (
                <MenuItem key={suite} value={suite}>
                  {suite}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Automation Status</InputLabel>
            <Select
              value={selectedAutomationStatus}
              label="Automation Status"
              onChange={handleAutomationStatusChange}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              {filters.automationStatuses?.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Summary Header - Always visible */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Test Cases Automation Summary ({summaryData.length} automation statuses)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Total Tests: {grandTotal} across {testSuites.length} test suites
        </Typography>
      </Box>

      {/* Conditional Content: Table or No Data Message */}
      {summaryData.length === 0 ? (
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent="center" 
          height="100%"
          minHeight="300px"
          component={Paper}
          sx={{ p: 4 }}
        >
          <Typography variant="body2" color="text.secondary">
            No automation summary data available
          </Typography>
        </Box>
      ) : (
        <TableContainer 
          component={Paper}
          sx={{
            borderRadius: 2,
            border: '1px solid rgba(0, 0, 0, 0.06)',
            boxShadow: 2,
            overflow: 'hidden',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'automationStatus'}
                    direction={orderBy === 'automationStatus' ? order : 'asc'}
                    onClick={createSortHandler('automationStatus')}
                    sx={{
                      '& .MuiTableSortLabel-icon': {
                        transition: 'transform 0.2s',
                      },
                    }}
                  >
                    Automation Status
                  </TableSortLabel>
                </TableCell>
                {/* Dynamic test suite columns */}
                {testSuites.map((suite) => (
                  <TableCell key={suite} align="center">
                    <Typography variant="body2" fontWeight="medium">
                      {suite}
                    </Typography>
                  </TableCell>
                ))}
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'totalCount'}
                    direction={orderBy === 'totalCount' ? order : 'asc'}
                    onClick={createSortHandler('totalCount')}
                  >
                    Total
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((row, index) => (
                <TableRow 
                  key={index} 
                  hover
                  sx={{
                    '&:nth-of-type(even)': {
                      backgroundColor: 'rgba(0, 0, 0, 0.01)',
                    },
                  }}
                >
                  <TableCell component="th" scope="row">
                    <Typography variant="body2" fontWeight="medium">
                      {row.automationStatus}
                    </Typography>
                  </TableCell>
                  {/* Dynamic test suite data columns */}
                  {testSuites.map((suite) => {
                    const count = (row.testSuiteCounts && row.testSuiteCounts[suite]) || 0;
                    return (
                      <TableCell key={suite} align="center">
                        <Chip
                          label={count}
                          color={count > 0 ? "primary" : "default"}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    );
                  })}
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="medium">
                      {row.totalCount}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              
              {/* Totals Row */}
              <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                <TableCell component="th" scope="row">
                  <Typography variant="body2" fontWeight="bold">
                    Total
                  </Typography>
                </TableCell>
                {/* Dynamic test suite totals */}
                {testSuites.map((suite) => (
                  <TableCell key={suite} align="center">
                    <Chip
                      label={testSuiteTotals[suite]}
                      color="primary"
                      size="small"
                      variant="filled"
                    />
                  </TableCell>
                ))}
                <TableCell align="center">
                  <Typography variant="body2" fontWeight="bold">
                    {grandTotal}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AutomationStatusTable;
